// =============================================================================
// client/src/pages/campaigns/CampaignContactImport.jsx | v2.0 | SQ-Chrome S1 | P9b — W-3
// v2.0 (SQ-Chrome S1, 2026-08-27): CHROME ONLY. Board vocabulary from
//       lib/flux/board.js. NO DATA LOGIC TOUCHED: every fetch, useState, ref,
//       handler, FormData field and server-formatted string is byte-identical
//       to v1.2. The global .card / .input / .label-dark / .btn-* classes were
//       replaced with board constants INLINE — index.css is shared with pages
//       outside this pass and was not edited.
// v1.2 (S81): the mapping state is FIELD-KEYED { field: headerIndex }, matching
//       the server. v1.1 had it inverted at four sites and the form could not
//       work: the dropdowns rendered empty against a good proposed_mapping, the
//       Import button never enabled, and a submission would have been refused
//       'unknown mapping key: 0'. The reducers now live in lib/flux/
//       importMapping.js so node --test can run them against the REAL validator.
// =============================================================================
// A FORM over P8's built importer. Upload → Map → Confirm → Done, plus history.
//
// 🔴 THE CLIENT READS NO CSV TEXT. It never parses, never sniffs a delimiter,
// never auto-maps and never batches. The server owns parse, map and run; this
// page uploads a file and renders what comes back. RM did all of that in the
// browser and sent a per-batch replaceAll flag with it.
//
// THE SERVER HOLDS NOTHING BETWEEN CALLS. There is no preview_id: preview is
// stateless, so Confirm RE-UPLOADS THE SAME FILE along with the mapping, the
// mode, and the `expected_headers` captured at preview. If the file changed
// underneath, the server answers 409 `file changed since preview` (T-18's
// sibling) and this page says so plainly.
//
// GATING (T-18): the client gates the whole PAGE on contacts.import, while the
// SERVER gates only the write — POST /import is requireCan('contacts.import')
// and POST /import/preview deliberately is not, because preview writes nothing.
// Do NOT "fix" that asymmetry, and do not rely on this page's gate for the
// write.
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useMe } from '../../lib/flux/useMe.js';
import { can } from '../../lib/can.js';
import Toast from '../../components/Toast.jsx';
import ConfirmModal from '../../components/ConfirmModal.jsx';
import { SKIP, selectedFieldFor, applyMapping, isEmailMapped } from '../../lib/flux/importMapping.js';
import {
  PAGE, SHELL, PANEL, PANEL_QUIET, DASH, BTN_DARK, BTN_LIGHT, BTN_GHOST,
  pill, FIELD_BOX, H1, H2, LABEL, BODY_MUTED,
} from '../../lib/flux/board.js';

const STEPS = ['Upload', 'Map', 'Confirm', 'Done'];

// The form column stays narrower than SHELL: a mapping row with a label and a
// select is unreadable stretched to the full board width. SHELL still sets the
// page gutter so this page lines up with the other four.
const FORM_COL = 'max-w-3xl';

export default function CampaignContactImport() {
  const navigate = useNavigate();
  const { role, loading: meLoading } = useMe();

  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [mode, setMode] = useState('upsert');
  const [sourceName, setSourceName] = useState('');
  const [listMonth, setListMonth] = useState('');   // 'YYYY-MM', stated by the operator, never taken from the clock
  const [replaceAck, setReplaceAck] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [toast, setToast] = useState(null);
  const [runs, setRuns] = useState([]);
  const inputRef = useRef(null);

  const loadRuns = useCallback(() => {
    api.get('/api/flux/contacts/import/runs?limit=20')
      .then((body) => setRuns(body?.runs || []))
      .catch(() => {});
  }, []);

  async function recoverStale(run) {
    const reason = window.prompt('Reason for marking this stale replace-all failed/fenced:');
    if (!reason?.trim()) return;
    try {
      await api.post(`/api/flux/contacts/import/runs/${run.id}/recover-stale`, { reason: reason.trim() });
      setToast({ message: 'Stale run marked failed and remains fenced. Retry the full replacement import.', tone: 'success' });
      loadRuns();
    } catch (err) { setToast({ message: err.body?.error || err.message, tone: 'error' }); }
  }

  async function verifyRepair(run) {
    const reason = window.prompt('Reason for verified fence repair (the server recomputes and compares sealed evidence):');
    if (!reason?.trim()) return;
    try {
      await api.post(`/api/flux/contacts/import/runs/${run.id}/verify-repair`, { reason: reason.trim() });
      setToast({ message: 'Fence cleared after server-side sealed-manifest verification.', tone: 'success' }); loadRuns();
    } catch (err) { setToast({ message: err.body?.error || err.message, tone: 'error' }); }
  }

  useEffect(() => { loadRuns(); }, [loadRuns]);

  if (meLoading) {
    return <div className={PAGE}><div className={`${BODY_MUTED} py-24 text-center`}>Loading…</div></div>;
  }

  // Redirect rather than render a page whose one action would 403.
  if (!can(role, 'contacts.import')) {
    return (
      <div className={PAGE}>
        <div className={`${SHELL} py-24 text-center`}>
          <h1 className={`${H2} mb-3`}>Import contacts</h1>
          <p className={`${BODY_MUTED} mb-8`}>
            Importing rewrites the shared coach directory, so it is Admin-only.
          </p>
          <button type="button" className={BTN_LIGHT} onClick={() => navigate('/contacts')}>
            Back to Contacts
          </button>
        </div>
      </div>
    );
  }

  async function doPreview(picked) {
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', picked);
      const body = await api.upload('/api/flux/contacts/import/preview', form);
      setPreview(body);
      setMapping(body.proposed_mapping || {});
      setSourceName(picked.name);
      setFile(picked);
      setStep(1);
    } catch (err) {
      // 400 (fewer than two records) and 413 (past the row/byte cap) both name
      // their own limit — render the server's message rather than guessing it.
      setToast({ message: err.body?.error || err.message, tone: 'error' });
    } finally {
      setBusy(false);
    }
  }

  async function doImport() {
    setConfirmOpen(false);
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);                                   // RE-UPLOAD
      form.append('mapping', JSON.stringify(mapping));
      form.append('mode', mode);
      form.append('source_name', sourceName || file.name);
      form.append('expected_headers', JSON.stringify((preview.headers || []).map((h) => h.name)));
      if (mode === 'replaceAll') form.append('confirm', 'replaceAll');
      if (mode === 'upsert' && listMonth) form.append('list_month', listMonth);

      const body = await api.upload('/api/flux/contacts/import', form);
      setResult(body);
      setStep(3);
      loadRuns();
    } catch (err) {
      const message = err.status === 409
        ? 'That file changed since the preview. Start again.'
        : (err.body?.error || err.message);
      setToast({ message, tone: 'error' });
      if (err.status === 409) { setStep(0); setPreview(null); setFile(null); }
    } finally {
      setBusy(false);
    }
  }

  const emailMapped = isEmailMapped(mapping);

  return (
    <div className={PAGE}>
      <div className={`${SHELL} py-10`}>

        <div className="flex items-start justify-between gap-6 mb-8">
          <div>
            <h1 className={H1}>Import contacts</h1>
            <p className={`${BODY_MUTED} mt-2`}>A CSV or TSV of college coaches</p>
          </div>
          <button type="button" className={`${BTN_GHOST} shrink-0 pt-3`} onClick={() => navigate('/contacts')}>
            ← Contacts
          </button>
        </div>

        {/* Step rail. Solid = current, solid-muted = done, dashed = ahead —
            the board's on/off pill vocabulary with one muted third state. */}
        <div className="flex flex-wrap gap-2 mb-8">
          {STEPS.map((label, i) => (
            <div
              key={label}
              className={
                i === step
                  ? pill(true)
                  : i < step
                    ? 'text-[0.65rem] font-semibold uppercase tracking-[0.12em] px-3 py-1.5 rounded-full border-2 bg-[#5A5A5A] text-white border-[#5A5A5A]'
                    : pill(false)
              }
            >
              {i + 1}. {label}
            </div>
          ))}
        </div>

        <div className={FORM_COL}>
          {step === 0 && (
            <div className={`${PANEL} p-6`}>
              <input
                ref={inputRef} type="file" accept=".csv,.tsv,.txt" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) doPreview(f); }}
              />
              <div
                className="border-2 border-dashed border-[#0E2A20] rounded-2xl py-20 text-center cursor-pointer hover:bg-[#F4F4F2] transition-colors"
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) doPreview(f); }}
              >
                <p className="font-display font-normal text-[#0E2A20] text-xl">
                  {busy ? 'Reading…' : 'Drop a .csv or .tsv here, or click to choose'}
                </p>
                <p className={`${LABEL} mt-3`}>The file is parsed on the server. Nothing is imported yet.</p>
              </div>
            </div>
          )}

          {step >= 1 && preview && (
            <MapStep
              preview={preview} mapping={mapping} setMapping={setMapping}
              mode={mode} setMode={setMode}
              sourceName={sourceName} setSourceName={setSourceName}
              listMonth={listMonth} setListMonth={setListMonth}
              replaceAck={replaceAck} setReplaceAck={setReplaceAck}
              emailMapped={emailMapped}
              busy={busy} step={step}
              onConfirm={() => setConfirmOpen(true)}
            />
          )}

          {step === 3 && result && <DoneStep result={result} onAgain={() => {
            setStep(0); setPreview(null); setFile(null); setResult(null); setReplaceAck(false); setMode('upsert');
          }} />}

          <EducationPanels />
        </div>

        <RunHistory runs={runs} onRecoverStale={recoverStale} onVerifyRepair={verifyRepair} />
      </div>

      <ConfirmModal
        open={confirmOpen}
        destructive={mode === 'replaceAll'}
        requireTyped={mode === 'replaceAll' ? 'REPLACE' : null}
        title={mode === 'replaceAll' ? 'Replace the whole directory' : 'Import contacts'}
        body={mode === 'replaceAll'
          ? `Deletes every contact except unsubscribed and bounced, then imports this file as the new master list. Suppressed addresses stay suppressed.\n\nThis file has ${preview?.file?.data_rows ?? '—'} data rows.`
          : `Imports ${preview?.file?.data_rows ?? '—'} data rows, updating any coach already in the directory.`}
        confirmLabel={mode === 'replaceAll' ? 'Replace directory' : 'Import'}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={doImport}
      />

      <Toast message={toast?.message} tone={toast?.tone} onDismiss={() => setToast(null)} />
    </div>
  );
}

function MapStep({
  preview, mapping, setMapping, mode, setMode, sourceName, setSourceName, listMonth, setListMonth,
  replaceAck, setReplaceAck, emailMapped, busy, step, onConfirm,
}) {
  const targets = ['email', 'first_name', 'last_name', 'title', 'school', 'division',
    'conference', 'state', 'city', 'region', 'phone', 'twitter', 'website'];

  return (
    <div className="space-y-5">
      <div className={`${PANEL_QUIET} p-5`}>
        <div className={BODY_MUTED}>
          <strong className="text-[#0E2A20]">{preview.file?.name}</strong> ·{' '}
          {preview.file?.data_rows} data rows · delimiter{' '}
          <code>{preview.file?.delimiter === '\t' ? 'TAB' : preview.file?.delimiter}</code> ·
          header row {preview.file?.header_row} · {preview.file?.encoding}
        </div>
        {preview.unmapped_columns?.length > 0 && (
          <div className={`${LABEL} mt-3 normal-case tracking-normal text-xs`}>
            Seen and not imported: {preview.unmapped_columns.join(', ')}
          </div>
        )}
      </div>

      <div className={`${PANEL} p-6`}>
        <div className={`${LABEL} mb-4`}>Column mapping</div>
        <div className="space-y-2">
          {(preview.headers || []).map((h) => (
            <div key={h.index} className="flex items-center gap-3">
              <span className="text-sm w-1/2 truncate text-[#0E2A20]" title={h.name}>{h.name}</span>
              <select
                className={`${FIELD_BOX} w-1/2`}
                value={selectedFieldFor(mapping, h.index)}
                onChange={(e) => setMapping((m) => applyMapping(m, h.index, e.target.value))}
              >
                <option value={SKIP}>— skip —</option>
                {targets.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          ))}
        </div>
        {!emailMapped && (
          <p className="text-xs text-red-700 mt-4">One column must map to <code>email</code>.</p>
        )}
      </div>

      <div className={`${PANEL} p-6`}>
        <div className={`${LABEL} mb-3`}>Source name</div>
        <input className={`${FIELD_BOX} w-full`} value={sourceName} onChange={(e) => setSourceName(e.target.value)} />
        <p className={`${BODY_MUTED} text-xs mt-2`}>Recorded on the run so this import is findable later.</p>
      </div>

      <div className={`${PANEL} p-6`}>
        <div className={`${LABEL} mb-3`}>Mode</div>
        <label className="flex items-center gap-2 text-sm mb-2 text-[#0E2A20]">
          <input type="radio" checked={mode === 'upsert'} onChange={() => { setMode('upsert'); setReplaceAck(false); }} />
          <span>Add and update (upsert) — leaves everything else alone</span>
        </label>
        {mode === 'upsert' && (
          <label className="flex items-center gap-2 text-xs mb-2 ml-6 text-[#0E2A20]">
            <span>Month of this list</span>
            <input type="month" value={listMonth} onChange={(e) => setListMonth(e.target.value)} className="border-2 border-[#0E2A20] rounded px-2 py-1" />
            <span className="opacity-70">Coaches this import adds become the "New from {listMonth || '…'} List" group in the campaign builder. Leave blank for a one-off edit.</span>
          </label>
        )}
        <label className="flex items-center gap-2 text-sm text-[#0E2A20]">
          <input type="radio" checked={mode === 'replaceAll'} onChange={() => setMode('replaceAll')} disabled={!emailMapped} />
          <span>Replace the whole directory</span>
        </label>

        {mode === 'replaceAll' && (
          <div className="mt-4 bg-amber-50 border-2 border-amber-500 text-amber-900 rounded-2xl p-4 text-sm">
            <p className="mb-3">
              Deletes every contact except unsubscribed and bounced, then imports this file as the new
              master list. Suppressed addresses stay suppressed.
            </p>
            <label className="flex items-center gap-2 text-xs font-bold">
              <input type="checkbox" checked={replaceAck} onChange={(e) => setReplaceAck(e.target.checked)} />
              I understand this rewrites the shared directory.
            </label>
          </div>
        )}
      </div>

      {step === 1 && (
        <div className="flex justify-end">
          <button
            type="button" className={`${BTN_DARK} disabled:opacity-40 disabled:shadow-none`}
            disabled={busy || !emailMapped || (mode === 'replaceAll' && !replaceAck)}
            onClick={onConfirm}
          >
            {busy ? 'Importing…' : mode === 'replaceAll' ? 'Replace directory…' : 'Import'}
          </button>
        </div>
      )}
    </div>
  );
}

function DoneStep({ result, onAgain }) {
  const run = result.run || {};
  const errors = result.report?.errors || [];
  return (
    <div className={`${PANEL} p-6 space-y-5`}>
      <h2 className={H2}>Import complete</h2>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
        {['rows_seen', 'inserted', 'updated', 'skipped', 'deleted'].map((k) => (
          <div key={k}>
            <div className={LABEL}>{k.replace('_', ' ')}</div>
            <div className="font-display font-normal text-[#0E2A20] text-3xl leading-none mt-1">{run[k] ?? 0}</div>
          </div>
        ))}
      </div>
      <div className={`${DASH} pt-4 ${BODY_MUTED}`}>
        Status <strong className="text-[#0E2A20]">{run.status}</strong> · source {run.source_name} · mode {run.mode}
      </div>
      {errors.length > 0 && (
        <div className="bg-red-50 border-2 border-red-700 rounded-2xl p-4 text-xs">
          <div className="font-bold text-red-700 mb-2">
            {result.report?.errors_total ?? errors.length} row(s) skipped
          </div>
          {errors.slice(0, 10).map((e, i) => (
            <div key={i} className="text-red-700">row {e.row}: {e.email} — {e.reason}</div>
          ))}
          {(result.report?.errors_total ?? errors.length) > 10 && (
            <div className="text-red-700 mt-1">and {(result.report.errors_total) - 10} more</div>
          )}
        </div>
      )}
      <div><button type="button" className={BTN_LIGHT} onClick={onAgain}>Import another file</button></div>
    </div>
  );
}

// Both RM education panels port, and both claims are TRUE in CCF: the queue is
// frozen at create, and timezones are resolved at ingest.
function EducationPanels() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
      <div className={`${PANEL_QUIET} p-5 ${BODY_MUTED}`}>
        <div className="font-display font-normal text-[#0E2A20] text-lg mb-2">Importing does not touch live campaigns</div>
        A campaign&apos;s recipient list is built when the campaign is created. Importing new coaches
        will not add them to a campaign that already exists.
      </div>
      <div className={`${PANEL_QUIET} p-5 ${BODY_MUTED}`}>
        <div className="font-display font-normal text-[#0E2A20] text-lg mb-2">Time zones are worked out on import</div>
        Each coach&apos;s send window is resolved from his state and school as the file is imported, so
        sends land during his working hours, not yours.
      </div>
    </div>
  );
}

function RunHistory({ runs, onRecoverStale, onVerifyRepair }) {
  if (!runs.length) return null;
  return (
    <div className="mt-12">
      <h2 className={`${H2} mb-4`}>Import history</h2>
      <div className={`${PANEL} p-5 overflow-x-auto`}>
        <table className="w-full text-sm">
          <thead>
            <tr className={`text-left ${LABEL} border-b-2 border-dashed border-[#0E2A20]`}>
              <th className="py-3 pr-3">Started</th>
              <th className="py-3 pr-3">Source</th>
              <th className="py-3 pr-3">Mode</th>
              <th className="py-3 pr-3">Seen</th>
              <th className="py-3 pr-3">Ins</th>
              <th className="py-3 pr-3">Upd</th>
              <th className="py-3 pr-3">Skip</th>
              <th className="py-3 pr-3">Del</th>
              <th className="py-3 pr-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className="border-b border-[#0E2A20]/15 text-[#0E2A20]">
                <td className="py-3 pr-3">{r.started_at}</td>
                <td className="py-3 pr-3">{r.source_name}</td>
                <td className="py-3 pr-3">{r.mode}</td>
                <td className="py-3 pr-3">{r.rows_seen}</td>
                <td className="py-3 pr-3">{r.inserted}</td>
                <td className="py-3 pr-3">{r.updated}</td>
                <td className="py-3 pr-3">{r.skipped}</td>
                <td className="py-3 pr-3">{r.deleted}</td>
                <td className="py-3 pr-3">
                  {r.status}
                  {/* The server decides staleness and says so; there is no
                      browser timer anywhere on this page (D-17). */}
                  {r.stale && (
                    <button type="button" onClick={() => onRecoverStale(r)} className="ml-2 text-amber-700 text-xs underline">
                      mark failed/fenced
                    </button>
                  )}
                  {r.status === 'failed' && !r.fence_cleared_at && r.manifest_sealed_at && (
                    <button type="button" onClick={() => onVerifyRepair(r)} className="ml-2 text-red-700 text-xs underline">
                      verify sealed repair
                    </button>
                  )}
                  {r.status === 'failed' && !r.manifest_sealed_at && <span className="ml-2 text-red-700 text-xs">retry-only (no sealed evidence)</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
